import type { Job, Stage } from "@/pages/jobs/types";
export type BoardJob = Job & {
  position: string;
  can_reorder: boolean;
  move_to: Stage[];
};
export type BoardColumn = {
  stage: Stage;
  total: number;
  first_id: string | null;
  last_id: string | null;
  jobs: BoardJob[];
  pagination: { page: number; pages: number };
};
export type BoardMove = {
  jobId: string;
  stage: Stage;
  before_id?: string;
  after_id?: string;
};
export const stages: Stage[] = [
  "to_do",
  "in_progress",
  "ready_for_collection",
  "billing",
  "done",
];
// A same-column drop follows the sortable preview. Rectangle centers can be
// exactly equal while a neighboring card has visibly moved into the source gap.
export function boardDrop(
  columns: BoardColumn[],
  activeId: string,
  overId: string,
  crossSide: "before" | "after" = "before",
) {
  const source = columns.find((column) =>
    column.jobs.some((job) => job.id === activeId),
  );
  const targetIndex = source?.jobs.findIndex((job) => job.id === overId) ?? -1;
  const side =
    source && targetIndex >= 0
      ? source.jobs.findIndex((job) => job.id === activeId) < targetIndex
        ? "after"
        : "before"
      : crossSide;
  return boardMove(columns, activeId, overId, side);
}
export function boardMove(
  columns: BoardColumn[],
  activeId: string,
  overId: string,
  side: "before" | "after" = "before",
): BoardMove | null {
  if (activeId === overId) return null;
  const source = columns.find((column) =>
    column.jobs.some((job) => job.id === activeId),
  );
  const job = source?.jobs.find((job) => job.id === activeId);
  const destination = columns.find(
    (column) =>
      `stage:${column.stage}` === overId ||
      column.jobs.some((job) => job.id === overId),
  );
  if (
    !job?.can_reorder ||
    !source ||
    !destination ||
    (destination.stage !== source.stage &&
      !job.move_to.includes(destination.stage))
  )
    return null;
  const remaining = destination.jobs.filter((item) => item.id !== activeId);
  const target = remaining.find((item) => item.id === overId);
  if (target)
    return {
      jobId: activeId,
      stage: destination.stage,
      [`${side}_id`]: target.id,
    };
  return {
    jobId: activeId,
    stage: destination.stage,
    ...(remaining.length
      ? { after_id: remaining[remaining.length - 1].id }
      : {}),
  };
}

export function boardEdgeMove(
  columns: BoardColumn[],
  jobId: string,
  stage: Stage,
  edge: "start" | "end",
): BoardMove | null {
  const source = columns.find((column) =>
    column.jobs.some((job) => job.id === jobId),
  );
  const job = source?.jobs.find((item) => item.id === jobId);
  const column = columns.find((item) => item.stage === stage);
  if (
    !job?.can_reorder ||
    !column ||
    (source?.stage !== stage && !job.move_to.includes(stage))
  )
    return null;
  const anchor = edge === "start" ? column.first_id : column.last_id;
  if (anchor === jobId) return null;
  return {
    jobId,
    stage,
    ...(anchor
      ? { [edge === "start" ? "before_id" : "after_id"]: anchor }
      : {}),
  };
}
